import { useState } from 'react'
import Button from '../../ui/Button'
import Modal from '../../ui/Modal'
import CopyButton from '../../ui/CopyButton'
import { createShare } from '../../api/client'
import { toShareRows } from '../../lib/share'
import { saveTest } from '../../lib/savedTests'
import { buildExport, downloadExport } from '../../lib/exportTest'
import { leakTest } from '../../content/site'
import type { DnsechoRow, IpDetails } from '@shared/api'
import styles from './ShareBar.module.css'

interface ShareBarProps {
    rows: DnsechoRow[]
    self: IpDetails | undefined
    hostName: string
    onRetest: () => void
}

/**
 * The finished-test actions: retest, share, export.
 *
 * Share now happens inside a dialog rather than mutating the bar in place -- the
 * old flow swapped the button for a raw link and left it there, which was easy
 * to miss and impossible to dismiss. The dialog is a native <dialog> (Escape and
 * backdrop both close it). Export is the local counterpart: it hands the visitor
 * a JSON file that never touches the network, which for a privacy tool is the
 * more honest way to "keep a copy".
 */
export default function ShareBar({ rows, self, hostName, onRetest }: ShareBarProps) {
    const [open, setOpen] = useState(false)
    const [link, setLink] = useState<string>()
    const [saved, setSaved] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string>()

    const empty = rows.length === 0

    async function publish(alsoSave: boolean) {
        setBusy(true)
        setError(undefined)
        try {
            const res = await createShare(toShareRows(rows), hostName || 'Not available')
            setLink(`${window.location.origin}/dns-leak-test/${res.id}`)
            if (alsoSave) {
                saveTest(res.id, res.createdDate)
                setSaved(true)
            }
        } catch {
            setError(leakTest.share.error)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className={styles.bar}>
            <Button onClick={onRetest}>{leakTest.actions.retest}</Button>

            <Button variant="primary" onClick={() => setOpen(true)} disabled={empty}>
                {leakTest.actions.share}
            </Button>

            <Button onClick={() => downloadExport(buildExport(rows, self))} disabled={empty}>
                {leakTest.actions.export}
            </Button>

            <Modal open={open} title={leakTest.share.title} onClose={() => setOpen(false)}>
                <p className={styles.intro}>{leakTest.share.intro}</p>

                {link ? (
                    <div className={styles.result}>
                        <code className={styles.link}>{link}</code>
                        <CopyButton value={link} label={leakTest.share.copy} copiedLabel={leakTest.share.copied} />
                        {saved && <p className={styles.saved}>{leakTest.share.saved}</p>}
                    </div>
                ) : (
                    <div className={styles.actions}>
                        <Button variant="primary" onClick={() => publish(false)} disabled={busy}>
                            {busy ? leakTest.share.creating : leakTest.share.create}
                        </Button>
                        <Button onClick={() => publish(true)} disabled={busy}>
                            {leakTest.share.alsoSave}
                        </Button>
                    </div>
                )}

                {error && <p role="alert" className={styles.error}>{error}</p>}
            </Modal>
        </div>
    )
}
